import React from "react";
import { Search, SlidersHorizontal, X } from "lucide-react";

const GRADE_ORDER = ["Pre-K", "PreK", "Kindergarten", "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7", "Grade 8"];
const rank = (g) => { const i = GRADE_ORDER.indexOf(g || ""); return i === -1 ? 99 : i; };

export const EMPTY_FILTERS = { q: "", subject: "", grade: "", tag: "", free: "", active: "", sort: "newest" };

export function isFiltering(f) {
  return !!(f.q || f.subject || f.grade || f.tag || f.free || f.active);
}

export function filterLessons(rows, f) {
  const t = f.q.trim().toLowerCase();
  let out = rows.filter((l) =>
    (!t || `${l.title || ""} ${l.subject_name || ""} ${l.tag || ""} ${l.objective || ""}`.toLowerCase().includes(t))
    && (!f.subject || l.subject_id === f.subject)
    && (!f.grade || l.grade === f.grade)
    && (!f.tag || (l.tag || "Lesson") === f.tag)
    && (!f.free || (f.free === "free" ? !!l.free : !l.free))
    && (!f.active || (f.active === "active" ? l.active !== false : l.active === false)));
  if (f.sort === "title") out = [...out].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  else if (f.sort === "grade") out = [...out].sort((a, b) => rank(a.grade) - rank(b.grade) || (a.title || "").localeCompare(b.title || ""));
  else if (f.sort === "oldest") out = [...out].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  return out;
}

const sel = "rounded-xl border-2 border-[#E5DECF] bg-white px-3 py-2 font-body text-sm text-[#1F2937] outline-none focus:border-[#0D9488]";

export default function LessonFilters({ rows, subjects, value, onChange, prefix, total, shown, showStatus = true }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const grades = [...new Set([...GRADE_ORDER.filter((g) => g !== "PreK"), ...rows.map((l) => l.grade).filter(Boolean)])].sort((a, b) => rank(a) - rank(b));
  const tags = [...new Set(rows.map((l) => l.tag || "Lesson"))].sort();
  const active = isFiltering(value);
  return (
    <div className="rounded-2xl border border-[#E5DECF] bg-white p-3" data-testid={`${prefix}-filters`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input value={value.q} onChange={(e) => set({ q: e.target.value })} data-testid={`${prefix}-search`} placeholder="Search lessons…" className={`${sel} w-full pl-9`} />
        </div>
        <select value={value.subject} onChange={(e) => set({ subject: e.target.value })} data-testid={`${prefix}-subject`} className={sel}>
          <option value="">All subjects</option>
          {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={value.grade} onChange={(e) => set({ grade: e.target.value })} data-testid={`${prefix}-grade`} className={sel}>
          <option value="">All grades</option>
          {grades.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        <select value={value.tag} onChange={(e) => set({ tag: e.target.value })} data-testid={`${prefix}-tag`} className={sel}>
          <option value="">All categories</option>
          {tags.map((g) => <option key={g} value={g}>{g}</option>)}
        </select>
        {showStatus && (
          <>
            <select value={value.free} onChange={(e) => set({ free: e.target.value })} data-testid={`${prefix}-free`} className={sel}>
              <option value="">Free & premium</option>
              <option value="free">Free only</option>
              <option value="premium">Not free</option>
            </select>
            <select value={value.active} onChange={(e) => set({ active: e.target.value })} data-testid={`${prefix}-active`} className={sel}>
              <option value="">Active & inactive</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
          </>
        )}
        <select value={value.sort} onChange={(e) => set({ sort: e.target.value })} data-testid={`${prefix}-sort`} className={sel}>
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="grade">Grade level</option>
          <option value="title">Title A–Z</option>
        </select>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 font-body text-xs text-[#6B7280]">
        <span data-testid={`${prefix}-count`}><SlidersHorizontal size={12} className="mr-1 inline" />{shown} of {total} lessons</span>
        {active && <button onClick={() => onChange({ ...EMPTY_FILTERS, sort: value.sort })} data-testid={`${prefix}-clear`} className="inline-flex items-center gap-1 rounded-full bg-[#F2ECE1] px-3 py-1 font-bold text-[#1F2937] hover:bg-[#E5DECF]"><X size={12} /> Clear filters</button>}
      </div>
    </div>
  );
}
